/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EditAbsenceDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-TIMELINE
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EditAbsenceDialog - Frontend UI module: EditAbsenceDialog.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парный END_METHOD, блоки save/delete с логами.
 * END_CHANGE_SUMMARY
 */

import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AppDateField from '../../../components/AppDateField';
import { hasAbsenceOverlap } from '../timelineUtils';

/* START_METHOD_EditAbsenceDialog */
/* START_CONTRACT: EditAbsenceDialog
 * PURPOSE: Диалог редактирования/удаления отсутствия сотрудника с проверкой пересечений.
 * INPUTS: { open: boolean, onClose: ()=>void, employeeName: string, absence: Absence|null, otherAbsences: Range[], onSave: (r)=>Promise, onDelete: ()=>Promise }.
 * OUTPUTS: { JSX.Element|null - диалог }.
 * SIDE_EFFECTS: вызовы onSave/onDelete (HTTP наверх); локальный state формы.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: EditAbsenceDialog
 */
export default function EditAbsenceDialog({
  open,
  onClose,
  employeeName,
  absence,
  otherAbsences = [],
  onSave,
  onDelete,
}) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!absence) return;
    setFrom(absence.from ?? '');
    setTo(absence.to ?? '');
    setError('');
  }, [absence]);

  if (!absence) return null;

  const hasBoth = Boolean(from && to);
  const rangeValid = hasBoth && from <= to;
  const overlaps = rangeValid && hasAbsenceOverlap({ from, to }, otherAbsences);
  const canSave = rangeValid && !overlaps && !loading;

  /* START_METHOD_handleSave */
  /* START_CONTRACT: handleSave
   * PURPOSE: Сохранить изменённый период отсутствия; при успехе закрыть, иначе показать ошибку.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onSave (HTTP); setLoading/setError; onClose.
   * LINKS: M-FE-TIMELINE
   * END_CONTRACT: handleSave
   */
  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);
    setError('');
    try {
      await onSave({ from, to });
      /* START_BLOCK_SAVE_OK */
      onClose();
      /* END_BLOCK_SAVE_OK */
    } catch (err) {
      /* START_BLOCK_SAVE_FAIL */
      console.warn('[EditAbsenceDialog][handleSave][SAVE_FAIL] save absence failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Не удалось сохранить отсутствие');
      /* END_BLOCK_SAVE_FAIL */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleSave */

  /* START_METHOD_handleDelete */
  /* START_CONTRACT: handleDelete
   * PURPOSE: Удалить отсутствие; при успехе закрыть, иначе показать ошибку.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onDelete (HTTP); setLoading/setError; onClose.
   * LINKS: M-FE-TIMELINE
   * END_CONTRACT: handleDelete
   */
  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      await onDelete();
      /* START_BLOCK_DELETE_OK */
      onClose();
      /* END_BLOCK_DELETE_OK */
    } catch (err) {
      /* START_BLOCK_DELETE_FAIL */
      console.warn('[EditAbsenceDialog][handleDelete][DELETE_FAIL] delete absence failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Не удалось удалить отсутствие');
      /* END_BLOCK_DELETE_FAIL */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleDelete */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>Редактирование отсутствия</DialogTitle>
      <DialogContent>
        {employeeName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {employeeName}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <AppDateField
            label="С"
            value={from}
            onChange={setFrom}
            disabled={loading}
            error={hasBoth && !rangeValid}
            sx={{ flex: 1 }}
          />
          <AppDateField
            label="По"
            value={to}
            onChange={setTo}
            disabled={loading}
            error={hasBoth && !rangeValid}
            helperText={hasBoth && !rangeValid ? 'Дата окончания раньше начала' : undefined}
            sx={{ flex: 1 }}
          />
        </Box>

        {overlaps && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Период пересекается с другим отсутствием сотрудника
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button
          color="error"
          size="small"
          onClick={handleDelete}
          disabled={loading}
        >
          Удалить
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={handleSave}
            disabled={!canSave}
          >
            Сохранить
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
/* END_METHOD_EditAbsenceDialog */
